import { useState } from "react";
import { type AnalysisEntry } from "@/lib/projectStore";
import { TECHNIQUES, type Technique, type TechniqueField } from "@/lib/techniquesData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X, ChevronDown, ChevronRight } from "lucide-react";

interface Props {
  analyses: AnalysisEntry[];
  onChange: (analyses: AnalysisEntry[]) => void;
}

export default function AnalysisPanel({ analyses, onChange }: Props) {
  const [selectedTechnique, setSelectedTechnique] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleAdd = () => {
    const technique = TECHNIQUES.find((t) => t.id === selectedTechnique);
    if (!technique) return;
    const init: Record<string, string | string[]> = {};
    technique.fields.forEach((f) => {
      init[f.id] = f.type === "list" ? [] : "";
    });
    const entry: AnalysisEntry = {
      id: crypto.randomUUID(),
      techniqueId: technique.id,
      techniqueName: technique.name,
      data: init,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onChange([...analyses, entry]);
    setExpandedId(entry.id);
    setSelectedTechnique("");
  };

  const updateEntry = (id: string, data: Record<string, string | string[]>) => {
    onChange(
      analyses.map((a) =>
        a.id === id ? { ...a, data, updatedAt: new Date().toISOString() } : a
      )
    );
  };

  const removeEntry = (id: string) => {
    onChange(analyses.filter((a) => a.id !== id));
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">
          Analyses ({analyses.length})
        </h2>
        <div className="flex gap-2">
          <Select value={selectedTechnique} onValueChange={setSelectedTechnique}>
            <SelectTrigger className="w-44 h-8 text-xs">
              <SelectValue placeholder="Choose technique…" />
            </SelectTrigger>
            <SelectContent>
              {TECHNIQUES.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={handleAdd} disabled={!selectedTechnique} className="gap-1">
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </div>
      </div>

      {analyses.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No analyses yet. Select a technique and click Add to start.
        </div>
      ) : (
        <div className="space-y-3">
          {analyses.map((entry) => {
            const technique = TECHNIQUES.find((t) => t.id === entry.techniqueId);
            const isExpanded = expandedId === entry.id;
            return (
              <div key={entry.id} className="border border-border rounded-md bg-card">
                <button
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                  onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="text-sm font-semibold font-body">{entry.techniqueName}</span>
                    <span className="text-[10px] text-muted-foreground font-mono">
                      {new Date(entry.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeEntry(entry.id);
                    }}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </button>
                {isExpanded && technique && (
                  <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                    {technique.fields.map((field) => (
                      <InlineFieldEditor
                        key={field.id}
                        field={field}
                        value={entry.data[field.id]}
                        onChange={(v) =>
                          updateEntry(entry.id, { ...entry.data, [field.id]: v })
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InlineFieldEditor({
  field,
  value,
  onChange,
}: {
  field: TechniqueField;
  value: string | string[];
  onChange: (v: string | string[]) => void;
}) {
  const [newItem, setNewItem] = useState("");

  if (field.type === "list") {
    const items = Array.isArray(value) ? value : [];
    const addItem = () => {
      if (!newItem.trim()) return;
      onChange([...items, newItem.trim()]);
      setNewItem("");
    };
    return (
      <div>
        <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1 block">
          {field.label}
        </label>
        <div className="space-y-1 mb-2">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-xs font-body bg-secondary/50 rounded px-2 py-1">
              <span className="flex-1">{item}</span>
              <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-1.5">
          <Input
            placeholder={field.placeholder}
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addItem()}
            className="text-xs h-7"
          />
          <Button variant="outline" size="sm" onClick={addItem} className="h-7 px-2">
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1 block">
        {field.label}
      </label>
      <Textarea
        placeholder={field.placeholder}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="text-xs"
      />
    </div>
  );
}
